import { SortMeta } from 'primeng/api';
import { MIQUserLob } from '@shared/constants/catalog.enum';
import { EntityType } from '@shared/models/entity-enum';
import {
  ActivityColumns,
  AvailabilityColumns,
  CompanyColumns,
  ContactColumns,
  LeaseColumns,
  PortfoliosaleSections,
  PropertyColumns,
  SaleColumns,
  SaleSections,
  SpaceColumns,
  TenancyColumns,
  TimColumns,
} from '@miq/catalog';
import { QueryCriteria } from './shared/search-filters.model';
import { CountryId } from '@shared/models/country.model';
import { getTokensFromLocalStorage } from '@miq/utils';

export const SUMMARY_SECTION_TITLE = 'summary';
export const DEFAULT_SECTION_TITLE = 'default';
export const EXCLUDED_SECTION_TITLES = [DEFAULT_SECTION_TITLE, SUMMARY_SECTION_TITLE];

/**
 * Source applications for CIQ record creation tracking
 */
export const CIQ_SOURCE_APPLICATIONS = {
  SFDC: 'SFDC',
} as const;

export const CIQ_SF_OBJECT_TYPES = {
  Account: 'Account',
  Contact: 'Contact',
} as const;

export const view = {
  table: 'table',
  map: 'map',
  both: 'both',
  card: 'card',
};
export interface SearchTab {
  tabName: string;
  mounted: boolean;
  viewEnabled: string;
  searchBarPlaceholder: string;
  aggregation?: any;
  state?: any;
  columnHeaders?: string[];
  textSearchFields?: string[];
  typeAhead?: boolean;
  isUsageFilterable?: boolean;
  isSubmarketFilterable?: boolean;
  hasMapEnabled?: boolean;
  restrictions?: Restriction;
  position?: number;
}

export interface Restriction {
  filters: RestrictionFilter[];
}

export interface RestrictionFilter {
  isEnabled: boolean;
  field: string;
  key?: number;
  value: QueryCriteria;
  categoryTitle: string;
}

export const DEFAULT_GEOGRAPHY_VALUES = {
  markets: [],
  propertyTypes: [],
  submarkets: [],
  districts: [],
  neighborhoods: [],
};

type MapOfQueryCriteria = { [key: string]: QueryCriteria };
const userInfo = JSON.parse(getTokensFromLocalStorage().userInfo || `{}`);
const isIndiaOrChinaUsr =
  (userInfo && userInfo.userCountrySkey === CountryId.India) ||
  (userInfo && userInfo.userCountrySkey === CountryId.China);

export const DEFAULT_FILTERS_PER_ENTITY: { [key: string]: MapOfQueryCriteria } = {
  [EntityType.Space]: {
    19025: {
      field: 'miq_record_hide_f',
      values: ['false'],
      type: 'equal',
      dataType: 'boolean',
      name: 'Hidden Spaces',
    },
  },
  [EntityType.Property]: {
    3724: {
      key: 3724,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    839: {
      key: 839,
      type: 'multiNotEqual',
      field: 'ref_property_status_desc',
      dataType: 'text',
      name: 'Status',
    },
    16933: {
      key: 16933,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Properties',
    },
  },
  [EntityType.Availability]: {
    3727: {
      key: 3727,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    2: {
      key: 2,
      type: 'multiNotEqual',
      field: 'ref_space_availability_status_desc',
      dataType: 'text',
      name: 'Availability Status',
    },
    17026: {
      key: 17026,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Availability Records',
    },
  },
  [EntityType.LeaseComp]: {
    1750: {
      key: 1750,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    1743: {
      key: 1743,
      type: 'multiNotEqual',
      field: 'ref_arrangement_status_desc',
      dataType: 'text',
      name: 'Comp Status',
    },
    17029: {
      key: 17029,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Lease Records',
    },
  },
  [EntityType.SaleComp]: {
    2298: {
      key: 2298,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    2273: {
      key: 2273,
      type: 'multiNotEqual',
      field: 'ref_arrangement_status_desc',
      dataType: 'text',
      name: 'Comp Status',
    },
    17030: {
      key: 17030,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Sale Records',
    },
  },
  [EntityType.Tim]: {
    16071: {
      key: 16071,
      type: 'multiNotEqual',
      field: 'status_type_desc',
      dataType: 'text',
      name: 'TIM Status',
    },
    17027: {
      key: 17027,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden TIM Records',
    },
  },
  [EntityType.Company]: {
    1364: {
      key: 1364,
      type: 'equal',
      field: 'edp_match_verified_f',
      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
    1359: { key: 1359, type: 'multiNotEqual', field: 'ref_party_status_desc', dataType: 'text', name: 'Status' },
  },
  [EntityType.Contact]: {
    1508: {
      key: 1508,
      type: 'equal',
      field: 'edp_match_verified_f',

      dataType: 'boolean',
      name: 'EDP Match Verified',
    },
  },
  [EntityType.Tenancy]: {
    3785: {
      key: 3785,
      type: 'multiNotEqual',
      field: 'property_space/tenancy_status',
      dataType: 'text',
      name: 'Status',
    },
    17028: {
      key: 17028,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Tenancy Records',
    },
  },
  [EntityType.Activity]: {
    [ActivityColumns.Admin_HideActivity]: {
      key: ActivityColumns.Admin_HideActivity,
      type: 'equal',
      field: 'miq_record_hide_f',
      dataType: 'boolean',
      name: 'Hidden Activity Records',
    },
  },
};

// create repository of `MapOfQueryCriteria` by Entity
const PropertyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Property];
const CompanyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Company];
const ContactFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Contact];
const LeaseCompFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.LeaseComp];
const SaleCompFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.SaleComp];
const AvailabilityFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Availability];
const TenancyFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Tenancy];
const TimFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Tim];
const SpaceFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Space];
const ActivityFilters: MapOfQueryCriteria = DEFAULT_FILTERS_PER_ENTITY[EntityType.Activity];

/**
 * Given a criteria map, returns a function to resolve the filter object of given columnId
 * and adds given `values` to QueryCriteria object to be returned
 *
 * @param queryCriteriaMap
 * @returns
 */
const restrictedQueryCriteriaMakerFactory =
  (queryCriteriaMap: MapOfQueryCriteria) => (columnId: number, values: any) => ({
    ...queryCriteriaMap[columnId],
    values,
  });

// create the QueryCriteria's factory functions by entity

// Property
const makePropertyRestrictionFilter = restrictedQueryCriteriaMakerFactory(PropertyFilters);

// Availability
const makeAvailabilityRestriction = restrictedQueryCriteriaMakerFactory(AvailabilityFilters);

// Lease
const makeLeaseRestrictionFilter = restrictedQueryCriteriaMakerFactory(LeaseCompFilters);

// SaleComp
const makeSaleCompRestrictionFilter = restrictedQueryCriteriaMakerFactory(SaleCompFilters);

// Tim
const makeTimRestrictionFilter = restrictedQueryCriteriaMakerFactory(TimFilters);

// Company
const makeCompanyRestrictionFilter = restrictedQueryCriteriaMakerFactory(CompanyFilters);

// Contact
const makeContactRestrictionFilter = restrictedQueryCriteriaMakerFactory(ContactFilters);

// Tenancy
const makeTenancyRestrictionFilter = restrictedQueryCriteriaMakerFactory(TenancyFilters);

// Space
const makeSpaceRestrictionFilter = restrictedQueryCriteriaMakerFactory(SpaceFilters);

// Activity
const makeActivityRestrictionFilter = restrictedQueryCriteriaMakerFactory(ActivityFilters);

/**
 * Restriction Filters Map Per Entity
 */
const RESTRICTION_FILTERS_PER_ENTITY = {
  [EntityType.Property]: [
    {
      isEnabled: true,
      key: PropertyColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makePropertyRestrictionFilter(PropertyColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: PropertyColumns.PropertyDetails_Status,
      field: 'ref_property_status_desc',
      value: makePropertyRestrictionFilter(PropertyColumns.PropertyDetails_Status, ['Deleted', 'Demolished']),
      categoryTitle: 'Building Details',
    },
    {
      isEnabled: true,
      key: PropertyColumns.Admin_HideProperty,
      field: 'miq_record_hide_f',
      value: makePropertyRestrictionFilter(PropertyColumns.Admin_HideProperty, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Availability]: [
    {
      isEnabled: true,
      key: AvailabilityColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeAvailabilityRestriction(AvailabilityColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: AvailabilityColumns.SpaceDetails_AvailabilityStatus,
      field: 'ref_space_availability_status_desc',
      value: makeAvailabilityRestriction(AvailabilityColumns.SpaceDetails_AvailabilityStatus, [
        'Deleted',
        'Off Market',
        'Renewed',
        'Sold',
        'Withdrawn',
      ]),
      categoryTitle: 'Space Details',
    },
    {
      isEnabled: true,
      key: 17026,
      field: 'miq_record_hide_f',
      value: makeAvailabilityRestriction(17026, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.LeaseComp]: [
    {
      isEnabled: true,
      key: LeaseColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_CompStatus,
      field: 'ref_arrangement_status_desc',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_CompStatus, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_HideLease,
      field: 'miq_record_hide_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_HideLease, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.LeaseExpire]: [
    {
      isEnabled: true,
      key: LeaseColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_CompStatus,
      field: 'ref_arrangement_status_desc',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_CompStatus, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: LeaseColumns.Admin_HideLease,
      field: 'miq_record_hide_f',
      value: makeLeaseRestrictionFilter(LeaseColumns.Admin_HideLease, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.SaleComp]: [
    {
      isEnabled: true,
      key: SaleColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeSaleCompRestrictionFilter(SaleColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: SaleColumns.Admin_CompStatus,
      field: 'ref_arrangement_status_desc',
      value: makeSaleCompRestrictionFilter(SaleColumns.Admin_CompStatus, ['Inactive']),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: 17030,
      field: 'miq_record_hide_f',
      value: makeSaleCompRestrictionFilter(17030, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Tim]: [
    {
      isEnabled: true,
      key: TimColumns.TIMDetails_TIMStatus,
      field: 'status_type_desc',
      value: makeTimRestrictionFilter(TimColumns.TIMDetails_TIMStatus, ['Closed']),
      categoryTitle: 'TIM Detail',
    },
    {
      isEnabled: true,
      key: 17027,
      field: 'miq_record_hide_f',
      value: makeTimRestrictionFilter(17027, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Company]: [
    {
      isEnabled: false,
      key: CompanyColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeCompanyRestrictionFilter(CompanyColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
    {
      isEnabled: true,
      key: CompanyColumns.Admin_Status,
      field: 'ref_party_status_desc',
      value: makeCompanyRestrictionFilter(CompanyColumns.Admin_Status, ['Inactive']),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Contact]: [
    {
      isEnabled: true,
      key: ContactColumns.Admin_EDPMatchVerified,
      field: 'edp_match_verified_f',
      value: makeContactRestrictionFilter(ContactColumns.Admin_EDPMatchVerified, 'true'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Tenancy]: [
    {
      isEnabled: true,
      key: TenancyColumns.FloorSuite_Status,
      field: 'property_space/tenancy_status',
      value: makeTenancyRestrictionFilter(TenancyColumns.FloorSuite_Status, ['Expired', 'Gone', 'No Longer Tenant']),
      categoryTitle: 'Floor/Suite',
    },
    {
      isEnabled: true,
      key: TenancyColumns.Admin_HideTenancy,
      field: 'miq_record_hide_f',
      value: makeTenancyRestrictionFilter(TenancyColumns.Admin_HideTenancy, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.Space]: [
    {
      isEnabled: true,
      key: 19025,
      field: 'miq_record_hide_f',
      value: makeSpaceRestrictionFilter(19025, 'false'),
      categoryTitle: 'Admin',
    },
  ],
  [EntityType.PortfolioSale]: [
    // empty but kept as placeholder
  ],
  [EntityType.Brand]: [
    // empty but kept as placeholder
  ],
  [EntityType.Activity]: [
    {
      isEnabled: true,
      key: ActivityColumns.Admin_HideActivity,
      field: 'miq_record_hide_f',
      value: makeActivityRestrictionFilter(ActivityColumns.Admin_HideActivity, 'false'),
      categoryTitle: 'Admin',
    }
  ],
};

/**
 * Tab configuration object, it holds information like title, position whether is mounted or not plus
 * restriction filters by entity
 *
 */
export const baseTabMap: SearchTab[] = [
  {
    /*
     * This entry is to fit with tab indexes (they star at 1 because "Records title" is a fake tab)
     * therefore we need this entry to match with calculated tabIndexes
     * better could be that "records" title were not a "fake tab",
     *
     * TODO: DEVELOPER NOTE: THIS should be considered when developing the new navigation
     */
    tabName: 'records title',
    mounted: true,
    viewEnabled: 'none',
    searchBarPlaceholder: 'records title',
    position: 0,
  },
  {
    tabName: 'records',
    mounted: false,
    viewEnabled: view.table,
    searchBarPlaceholder: ``,
    aggregation: {},
    hasMapEnabled: true,
  },
  {
    tabName: 'property',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchPropertyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Property),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'company',
    mounted: false,
    isUsageFilterable: false,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchCompanyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Company),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'brand',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchBrandPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Brand),
    },
    hasMapEnabled: false,
  },
  {
    tabName: 'contact',
    mounted: false,
    isUsageFilterable: false,
    isSubmarketFilterable: true,
    viewEnabled: view.table, // * Prevent to show the map
    searchBarPlaceholder: 'recordsSearch.searchContactPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Contact),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'lease',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchLeasePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.LeaseComp),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'sale',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchSalePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.SaleComp),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'portfoliosale',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchPortfolioSalePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.PortfolioSale),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'availability',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchAvailabilityPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Availability),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'tim',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: false,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchTimPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Tim),
    },
    hasMapEnabled: false,
  },
  {
    tabName: 'activity',
    mounted: false,
    isUsageFilterable: false,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchActivityPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Activity),
    },
    hasMapEnabled: false,
  },
  {
    tabName: 'tenancy',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchTenancyPlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Tenancy),
    },
    hasMapEnabled: true,
  },
  {
    tabName: 'space',
    mounted: false,
    isUsageFilterable: true,
    isSubmarketFilterable: true,
    viewEnabled: view.table,
    searchBarPlaceholder: 'recordsSearch.searchSpacePlaceHolder',
    aggregation: {},
    restrictions: {
      filters: restrictionFilters(EntityType.Space),
    },
    hasMapEnabled: true,
  },
];

export const baseFilterInitialState = {
  availability: [],
  property: [],
  company: [],
  lease: [],
  sale: [],
  portfoliosale: [],
  contact: [],
  tim: [],
  tenancy: [],
  brand: [],
  space: [],
  propertyspacesubtab: [],
  activity: [],
};

export const DEFAULT_GEO_LABELS = {
  country: 'recordsSearch.country',
  district: 'recordsSearch.district',
  globalRegion: 'recordsSearch.globalRegion',
  market: 'recordsSearch.markets',
  neighborhood: 'recordsSearch.neighborhood',
  division: 'recordsSearch.division',
  submarket: 'recordsSearch.submarket',
};

export const postalAddressPath = {
  property: 'postal_address',
  company: 'location/postal_address',
  lease_comp: 'property.postal_address',
  sale_comp: 'property.postal_address',
  availability: 'property.postal_address',
  tim: 'postal_address',
};

export const HOVER_LIMIT = 50;

export enum usagesAttributes {
  ClassType = 844,
  SubmarketDesc = 862,
  UsageTypeDesc = 1050,
}

export const usagesAttributesInfo = {
  [usagesAttributes.ClassType]: {
    field: 'usages/ref_property_class_type_desc',
  },
  [usagesAttributes.SubmarketDesc]: {
    field: 'usages/lig_geography.submarket_desc',
  },
  [usagesAttributes.UsageTypeDesc]: {
    field: 'usages/statistical_f',
  },
};

export const groupAttributesByEntity = {
  [EntityType.Availability]: {
    parent: 'property',
    child: 'availabilities',
  },
  [EntityType.Space]: {
    parent: 'property',
    child: 'spaces',
  },
};
export const DefaultMultiSortMeta = [
  { field: AvailabilityColumns.AvailabilityProperty_StreetName.toString(), order: 1 },
  { field: AvailabilityColumns.AvailabilityProperty_StreetNo.toString(), order: 2 },
];

export const DefaultMultiSortFields = [
  {
    columnId: AvailabilityColumns.AvailabilityProperty_StreetName,
    field: 'property.postal_address.street_name',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: AvailabilityColumns.AvailabilityProperty_StreetNo,
    field: 'property.postal_address.street1',
    dataType: 'text',
    order: 'asc',
  },
];

export const DefaultSpaceMultiSortMeta = [
  { field: SpaceColumns.Property_PropertyName.toString(), order: 1 },
  { field: SpaceColumns.SpaceDetails_Floor.toString(), order: -1 },
  { field: SpaceColumns.SpaceDetails_Unit.toString(), order: 1 },
];

export const DefaultSpaceMultiSortFields = [
  {
    columnId: SpaceColumns.Property_PropertyName,
    field: 'property.property_name',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: SpaceColumns.SpaceDetails_Floor,
    field: 'floor_suite.floor_number',
    dataType: 'text',
    order: 'desc',
  },
  {
    columnId: SpaceColumns.SpaceDetails_Unit,
    field: 'floor_suite.suite_number',
    dataType: 'text',
    order: 'asc',
  },
];

export const DefaultHKRetailSpaceClipboardMultiSortFields = [
  {
    columnId: SpaceColumns.Space_Submarket,
    field: 'property.property_usage.lig_geography.submarket_desc',
    sortIndex: 0,
  },
  {
    columnId: SpaceColumns.Property_District_Space,
    field: 'property.postal_address.district',
    sortIndex: 1,
  },
  {
    columnId: SpaceColumns.Property_StreetName,
    field: 'property.postal_address.street_name',
    sortIndex: 2,
  },
  {
    columnId: SpaceColumns.Property_StreetNo,
    field: 'property.postal_address.street1',
    sortIndex: 3,
  },
  {
    columnId: SpaceColumns.SpaceDetails_Floor,
    field: 'floor_suite.floor_number',
    sortIndex: 4,
  },
  {
    columnId: SpaceColumns.SpaceDetails_Unit,
    field: 'floor_suite.suite_number',
    sortIndex: 5,
  },
];

export const DefaultSpaceClipboardMultiSortFields = [
  {
    columnId: SpaceColumns.Property_District_Space,
    field: 'property.postal_address.district',
    sortIndex: 0,
  },
  {
    columnId: SpaceColumns.Property_PropertyName,
    field: 'property.property_name',
    sortIndex: 1,
  },
  {
    columnId: SpaceColumns.SpaceDetails_Floor,
    field: 'floor_suite.floor_number',
    sortIndex: 2,
  },
  {
    columnId: SpaceColumns.SpaceDetails_Unit,
    field: 'floor_suite.suite_number',
    sortIndex: 3,
  },
];

/**
 * Default multi-sort configuration for HK Retail Space entity (main Space tab grid state).
 * Street Name (asc, order 1) → Street Number (asc, order 2) → Floor (asc, order 3) → Unit (asc, order 4)
 * NOTE: Uses column IDs as field values — for PrimeNG grid display only, NOT for API calls.
 */
export const HKRetailSpaceMultiSortMeta = [
  { field: SpaceColumns.Property_StreetName.toString(), order: 1, sortIndex: 0 },
  { field: SpaceColumns.Property_StreetNo.toString(), order: 1, sortIndex: 1 },
  { field: SpaceColumns.SpaceDetails_Floor.toString(), order: 1, sortIndex: 2 },
  { field: SpaceColumns.SpaceDetails_Unit.toString(), order: 1, sortIndex: 3 },
];

/**
 * API-format multi-sort fields for HK Retail Space Popup / Property > Spaces subtab.
 * Floor (asc, order 1) → Unit (asc, order 2) — uses actual field paths for the API.
 */
export const HKRetailPopupSpaceMultiSortFields = [
  {
    columnId: SpaceColumns.SpaceDetails_Floor,
    field: 'floor_suite.floor_number',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: SpaceColumns.SpaceDetails_Unit,
    field: 'floor_suite.suite_number',
    dataType: 'text',
    order: 'asc',
  },
];

/**
 * Default multi-sort configuration for HK Retail Company > Spaces subtab.
 * District (asc, order 1) → Street Name (asc, order 2) → Street Number (asc, order 3) → Floor (asc, order 4) → Unit (asc, order 5)
 */
export const HKRetailCompanySpaceMultiSortMeta = [
  { field: SpaceColumns.Property_District_Space.toString(), order: 1, sortIndex: 0 },
  { field: SpaceColumns.Property_StreetName.toString(), order: 1, sortIndex: 1 },
  { field: SpaceColumns.Property_StreetNo.toString(), order: 1, sortIndex: 2 },
  { field: SpaceColumns.SpaceDetails_Floor.toString(), order: 1, sortIndex: 3 },
  { field: SpaceColumns.SpaceDetails_Unit.toString(), order: 1, sortIndex: 4 },
];

export const HKRetailSpaceMultiSortFields = [
  {
    columnId: SpaceColumns.Property_StreetName,
    field: 'property.postal_address.street_name',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: SpaceColumns.Property_StreetNo,
    field: 'property.postal_address.street1',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: SpaceColumns.SpaceDetails_Floor,
    field: 'floor_suite.floor_number',
    dataType: 'text',
    order: 'asc',
  },
  {
    columnId: SpaceColumns.SpaceDetails_Unit,
    field: 'floor_suite.suite_number',
    dataType: 'text',
    order: 'asc',
  },
];

export function getDefaultSpaceMultiSortMeta(userLobSkey: number): SortMeta[] {
  return userLobSkey === MIQUserLob.HKG_Retail ? [...HKRetailSpaceMultiSortMeta] : [...DefaultSpaceMultiSortMeta];
}

/**
 * Returns the API-format (field-path) multi-sort for the Space Popup / Property > Spaces subtab.
 * HK Retail: Floor (asc) → Unit (asc).
 * Others: DefaultSpaceMultiSortFields.
 */
export function getDefaultSpacePopupMultiSortFields(userLobSkey: number) {
  return userLobSkey === MIQUserLob.HKG_Retail ? [...HKRetailPopupSpaceMultiSortFields] : [...DefaultSpaceMultiSortFields];
}

export function getDefaultCompanySpaceMultiSortMeta(userLobSkey: number): SortMeta[] {
  return userLobSkey === MIQUserLob.HKG_Retail ? [...HKRetailCompanySpaceMultiSortMeta] : [...DefaultSpaceMultiSortMeta];
}

/**
 * HK Catalog Source Application Defaults
 * Pre-selected source apps based on HK catalog (Office, Retail, Industrial)
 */
export const HK_CATALOG_SOURCE_APPS = {
  [MIQUserLob.HKG_Retail]: ['GRID', 'SFDC', 'MIQ'],
  [MIQUserLob.HKG_Office]: ['UNICORN', 'SFDC', 'MIQ'],
  [MIQUserLob.HKG_Industrial]: ['MIQ', 'SFDC', 'FILEUPLOAD'],
};

/**
 * Korea Company/Contact dialog source-app defaults.
 */
export const KOREA_CATALOG_SOURCE_APPS = ['SFDC', 'FINISHLINE', 'GRID'];

export const POPUP_TYPE = {
  recordList: 'record-list',
  tableSettings: 'table-settings',
};

export const DefaultFilterForBrandIndia = {
  type: 'equal',
  field: 'brand_create_country_desc',
  key: 18260,
  dataType: 'text',
  name: 'Brand Create Country',
  values: 'India',
};

export const DefaultFilterForBrandSpain = {
  type: 'equal',
  field: 'brand_create_country_desc',
  key: 18260,
  dataType: 'text',
  name: 'Brand Create Country',
  values: 'Spain',
};

export const DefaultFilterForBrandNonIndia = {
  type: 'equal',
  field: 'brand_create_country_desc',
  key: 18260,
  dataType: 'text',
  name: 'Brand Create Country',
  includeEmpty: true,
  excludeEmpty: false,
};

export const DefaultCountryForBrandNonSpainCE = {
  type: 'multiNotEqual',
  field: 'brand_create_country_desc',
  key: 18260,
  dataType: 'text',
  name: 'Brand Create Country',
  values: ['Spain'],
  includeEmpty: true,
  excludeEmpty: false,
};

/**
 * Default filter configuration for Brand CE Retail.
 */
export const DefaultFilterForBrandCERetail = {
  type: 'equal',
  field: 'division_desc',
  key: 18503,
  dataType: 'text',
  name: 'Brand Create Division',
  values: 'Continental Europe',
};

/**
 * Default filter configuration for Brand Non-CE Retail.
 */
export const DefaultFilterForBrandNonCERetail = {
  type: 'equal',
  field: 'division_desc',
  key: 18503,
  dataType: 'text',
  name: 'Brand Create Division',
  includeEmpty: true,
  excludeEmpty: false,
};

export function restrictionFilters(entity) {
  return isIndiaOrChinaUsr ? RESTRICTION_FILTERS_PER_ENTITY[entity] : excludeEDPMatchVerified(entity);
}
function excludeEDPMatchVerified(entity) {
  return RESTRICTION_FILTERS_PER_ENTITY[entity].filter(ele => ele.field !== 'edp_match_verified_f');
}
export const RestrictPortfolioSaleWithoutPropertyRecords = {
  type: 'equal',
  field: 'salecomps/property.id',
  key: 16991,
  dataType: 'text',
  name: 'Property ID',
  includeEmpty: false,
  excludeEmpty: true,
};

export const entityColumnApiConst = {
  [EntityType.PortfolioSale]: {
    apiCallSuccess: false,
    isEntityNoProp: false,
    NoPropNumber: PortfoliosaleSections.PortfolioSaleNoProperty,
  },
  [EntityType.SaleComp]: {
    apiCallSuccess: false,
    isEntityNoProp: false,
    NoPropNumber: SaleSections.SaleCompNoProperty,
  },
};
export const MAX_LIMIT = 200;

export const searchV2API = {
  property: true,
  availability: true,
  saleComp: true,
  lease: true,
  tenant: true,
  sale: true,
  portfoliosale: false,
  company: true,
  contact: false,
  tim: false,
  tenancy: true,
  brand: false,
  space: true,
  activity: true,
};

// L2-S4: moved to @miq/shared-models (constants/measurement-column-maps.const.ts) so the
// measurement formatters could move into @miq/platform-ui. Re-exported for compat.
export {
  columnMeasurementMap,
  columnValueMeasurementMap,
  columnMinMaxMeasurementMap,
  formatMeasurementDescription,
} from '@miq/shared-models';
